// ComplexFacet — pick a development (კომპლექსი). B1145 / owner 2026-08-28.
//
// 🔴 THIS FILTER HAS WORKED END TO END SINCE 0729 AND HAD NO CONTROL. It is committed, in the
// URL, and pushed down to SQL as p_complex_ids — reachable only by clicking a suggestion in the
// search box. 332 developments, 1,874 properties, and an agent who did not know to type the name
// could not ask the question at all. It is the clearest case of the gap the filter registry
// exists to stop: a filter is not shipped until it has a way in.
//
// TYPE-AHEAD, NOT A LIST. 332 rows is too many to render and too few to paginate, and an agent
// searching a development already knows its name — they are confirming, not browsing. Same shape
// as the street rail for the same reason.
//
// ⚠️ THE SELECTED ROWS MUST NAME THEMSELVES WITHOUT THE SEARCH. A URL-restored filter arrives as
// bare uuids, and the type-ahead cannot label them because nothing has been typed. So selections
// resolve through getComplexesByIds — the same reason that helper exists for the criteria editor:
// "a picker that cannot name what is already selected shows uuids, and a field showing uuids is
// read-only in practice."
import React, { useEffect, useRef, useState } from 'react'

export default function ComplexFacet({ selected = [], onToggle, search, resolveByIds, t }) {
  const tr = t || (k => k)
  const [q, setQ] = useState('')
  const [hits, setHits] = useState([])
  const [names, setNames] = useState({})     // id -> label, for the selected rows
  const [busy, setBusy] = useState(false)
  const seq = useRef(0)

  // Type-ahead. The endpoint itself refuses under 2 characters, so the guard mirrors it rather
  // than firing a request that is known to return nothing.
  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) { setHits([]); return }
    const mine = ++seq.current
    setBusy(true)
    let alive = true
    const timer = setTimeout(async () => {
      try {
        const rows = await search(term)
        // ⚠️ Drop a stale answer: a slow request for "va" must not overwrite a fast one for
        // "vake". Without the sequence check the list flickers back to the shorter query.
        if (alive && mine === seq.current) setHits(Array.isArray(rows) ? rows : [])
      } catch { if (alive && mine === seq.current) setHits([]) }
      finally { if (alive && mine === seq.current) setBusy(false) }
    }, 220)
    return () => { alive = false; clearTimeout(timer) }
  }, [q, search])

  // Name whatever is selected, including ids that arrived from a shared link.
  useEffect(() => {
    const unknown = selected.filter(id => !names[id])
    if (!unknown.length) return
    let alive = true
    resolveByIds(unknown).then(rows => {
      if (!alive) return
      const add = {}
      for (const r of rows || []) if (r?.id) add[r.id] = r.name_ka || r.name_en || r.name || r.id
      if (Object.keys(add).length) setNames(n => ({ ...n, ...add }))
    }).catch(() => {})
    return () => { alive = false }
  }, [selected, names, resolveByIds])

  const label = (c) => c.name_ka || c.name_en || c.name || c.id

  return (
    <div className="space-y-2">
      {/* Selected first, and always visible — a chosen development must not disappear because
          the search box was cleared. */}
      {selected.length > 0 && (
        <div className="space-y-0.5">
          {selected.map(id => (
            <button key={id} type="button" onClick={() => onToggle(id)}
              className="w-full flex items-center gap-2 text-sm text-foreground hover:text-muted-foreground py-0.5 text-left">
              <span className="h-4 w-4 shrink-0 rounded border border-primary bg-primary/15 text-primary text-[11px] leading-none flex items-center justify-center">✓</span>
              <span className="flex-1 truncate">{names[id] || id}</span>
            </button>
          ))}
        </div>
      )}

      <input value={q} onChange={e => setQ(e.target.value)} type="search"
        placeholder={tr('desk_complex_search_ph')}
        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm" />

      {q.trim().length >= 2 && (
        <div className="space-y-0.5">
          {busy && <p className="text-xs text-muted-foreground">{tr('desk_loading')}</p>}
          {!busy && !hits.length && <p className="text-xs text-muted-foreground">{tr('desk_complex_none')}</p>}
          {hits.filter(c => !selected.includes(c.id)).map(c => (
            <button key={c.id} type="button" onClick={() => { onToggle(c.id); setQ('') }}
              className="w-full flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground py-0.5 text-left">
              <span className="h-4 w-4 shrink-0 rounded border border-input" />
              <span className="flex-1 truncate">{label(c)}</span>
              {c.developer && <span className="text-[11px] text-muted-foreground/70 truncate max-w-[40%]">{c.developer}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
